/**
 * La bibliothèque — « Ma chaîne » côté catalogue (SPEC-UI §3.4, tâche M2.24).
 *
 * ⟦A-5 étage 6⟧ Elle s'ouvre en même temps que la vidéo longue et prend la forme d'une
 * **étagère** dans la chambre (⟦A-7⟧ décision 5).
 *
 * ⟦A-9 arc 2⟧ **C'est l'écran du « métier »**, celui qui manquait entre 100 et 1 000 abonnés :
 * on ouvrait la vidéo longue et on lâchait le joueur. Cinq mécaniques étaient codées dans le
 * moteur sans jamais avoir d'écran — la bibliothèque elle-même, le **ménage**, le
 * **remaster**, les **séries** et la **rente evergreen**. Elles sont toutes ici, et chacune
 * dit *pourquoi* elle compte : une mécanique qui ne se voit pas ne s'apprend pas.
 *
 * ⟦A-8⟧ **C'est aussi ici que vit le suspense.** En mode « réel », la publication ne révèle
 * plus l'avenir d'une vidéo : la courbe se remplit jour après jour, sur sa fiche. La
 * bibliothèque a enfin une raison d'être consultée — elle n'en avait aucune.
 *
 * **Aucune règle n'est réinventée ici.** Les conditions viennent de l'arbre (`aCapacite`), les
 * coûts de `actionCost`, les seuils et les facteurs des constantes exportées (`malusNulles`,
 * `PLANCHER_NULLES`, `RENTE_SATURATION_RANG`, `RENTE_MULT_SERIE`…), le refus du moteur.
 * Le seul chiffre écrit ici est le minimum de 3 vidéos par série, que le moteur code en dur
 * dans `validateAction` (`series_too_small`) sans l'exporter.
 *
 * ⚠️ **Signalé, non tranché — deux actions n'ont pas d'effet moteur.** `remaster` et
 * `ranger_serie` sont **validées et facturées** par `engine/actions.ts`, mais aucun hook ne
 * les applique : `anchor_h` n'est jamais réarmé, aucune `Series` n'est jamais créée (le
 * commentaire « les actions restantes […] n'appliquent que leur coût » le dit lui-même). Le
 * précédent du repo est net — `clore_run` a été laissé sans bouton parce qu'« il aurait
 * menti ». Ici on ne les cache pas (⟦A-9⟧ veut les mécaniques atteignables) : on les affiche
 * **étiquetées comme des stubs**, à la manière du `CurseurStub` des mini-jeux, et le dialogue
 * de confirmation le dit avant de facturer. À rebrancher côté moteur.
 *
 * ⟦C57 · lot 5d · A-174⟧ **Moins de texte.** L'écran ne garde que le tableau, les chiffres de la
 * rente et des nulles, et des phrases de 12 mots au plus (`textes-courts.ts`, mesurées par un test).
 * Le « pourquoi » de chaque mécanique (la rente, le ménage, les séries, les deux stubs) est dans
 * « 📰 Les dessous » (`DessousBibliotheque.tsx`), mot pour mot ; les trois dialogues de confirmation
 * tiennent en deux ou trois lignes de chiffres, et disent toujours que le stub ne ferait rien.
 */

import { useMemo, useState } from "react";

import {
  actionCost,
  malusNulles,
  PLANCHER_NULLES,
  RENTE_MULT_SERIE,
  RENTE_SATURATION_RANG,
  statutAffichage,
  type VideoRecord,
} from "@maxime/shared";

import { Panneau } from "../../components/Panneau";
import { contenu } from "../../game/content";
import { BADGE_CLASSE, libelleCout, nombre } from "../../game/format";
import { capacite, modificateurs } from "../../game/moteur";
import { useRunStore } from "../../stores/runStore";
import { dialog, useUIStore } from "../../stores/uiStore";

import { DessousBibliotheque } from "./DessousBibliotheque";
import { FicheVideo } from "./FicheVideo";
import { renteHebdomadaire } from "./rente";
import {
  AUCUNE_SERIE_COURTE,
  CLIN_D_OEIL_COURT,
  NOTE_MENAGE_FERME_COURTE,
  NOTE_NULLE_COURTE,
  NOTE_SERIES_FERMEES_COURTE,
  corpsMenage,
  corpsRemaster,
  corpsSerie,
  ligneNullesCourte,
  ligneRenteCourte,
  ligneSerieCourte,
} from "./textes-courts";

/** Minimum de vidéos par série — `validateAction` jette `series_too_small` en dessous (§6.5). */
const SERIE_MIN = 3;
const NOM_SERIE_MIN = 3;
const NOM_SERIE_MAX = 32;

/**
 * Le nombre de nulles **tolérées** avant que le malus ne morde, déduit de `malusNulles`
 * plutôt que recopié : §6.5 dit « au-delà de 3 nulles visibles », mais c'est un ✎ d'équilibrage
 * (M3.12) et un chiffre recopié dérive.
 */
const SEUIL_NULLES = (() => {
  let n = 0;
  while (n < 100 && malusNulles(n + 1) === 1) n += 1;
  return n;
})();

/** Le coût d'une nulle de trop, lui aussi déduit (deux points de la courbe de `malusNulles`). */
const COUT_PAR_NULLE = malusNulles(SEUIL_NULLES + 1) - malusNulles(SEUIL_NULLES + 2);

export function Bibliotheque() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);
  const mode = useUIStore((s) => s.settings.revealMode);
  const [selection, setSelection] = useState<number[]>([]);
  const [ouverte, setOuverte] = useState<number | null>(null);

  const state = run?.state ?? null;
  const mods = useMemo(() => (state ? modificateurs(state) : null), [state]);
  const rente = useMemo(
    () => (state && mods ? renteHebdomadaire(state, mods) : null),
    [state, mods],
  );

  if (!state || !rente) return null;

  const c = contenu();
  const nom = (video: VideoRecord) => c.parSujet.get(video.subject_id)?.nom ?? video.subject_id;

  const nullesVisibles = state.videos.filter(
    (v) => v.class === "nulle" && v.visibility === "public",
  ).length;
  const malusActuel = malusNulles(nullesVisibles);
  const enTrop = Math.max(0, nullesVisibles - SEUIL_NULLES);

  const peutFaireLeMenage = capacite(state, "menage_bibliotheque");
  const peutRangerEnSerie = capacite(state, "series_bibliotheque");

  // La vidéo affichée en fiche : la sélectionnée, sinon la dernière publiée.
  const videos = [...state.videos].reverse();
  const affichee = videos.find((v) => v.id === ouverte) ?? videos[0] ?? null;
  const renteDeLaFiche = affichee
    ? (rente.lignes.find((l) => l.video.id === affichee.id) ?? null)
    : null;

  // ── Les trois actions ──────────────────────────────────────────────────────

  async function faireLeMenage(video: VideoRecord) {
    if (!state) return;
    const apres = malusNulles(nullesVisibles - 1);
    // ⟦C57 · lot 5d⟧ Le nom, l'effet en chiffres, le coût : le « pourquoi » est dans les dessous.
    const ok = await dialog.confirm({
      title: "Passer cette vidéo en privé ?",
      body: corpsMenage({
        nomVideo: nom(video),
        avant: malusActuel,
        apres,
        cout: libelleCout(actionCost({ type: "menage", video_id: video.id }, state)),
      }),
      confirmLabel: "Faire le ménage",
    });
    if (ok) await act({ type: "menage", video_id: video.id });
  }

  async function remasteriser(video: VideoRecord) {
    if (!state) return;
    const ok = await dialog.confirm({
      title: "Remasteriser cette evergreen ?",
      body: corpsRemaster({
        nomVideo: nom(video),
        cout: libelleCout(actionCost({ type: "remaster", video_id: video.id }, state)),
      }),
      confirmLabel: "Remasteriser quand même",
    });
    if (ok) await act({ type: "remaster", video_id: video.id });
  }

  async function rangerEnSerie() {
    if (!state || selection.length < SERIE_MIN) return;
    const nomSerie = await dialog.prompt({
      title: "Ranger en série",
      label: "Nom de la série",
      body: corpsSerie({ combien: selection.length, mult: RENTE_MULT_SERIE }),
      placeholder: "J'ai percé",
      valider: (v) => {
        const propre = v.trim();
        if (propre.length < NOM_SERIE_MIN || propre.length > NOM_SERIE_MAX) {
          return `Entre ${NOM_SERIE_MIN} et ${NOM_SERIE_MAX} caractères.`;
        }
        return null;
      },
    });
    if (!nomSerie) return;
    const resultat = await act({
      type: "ranger_serie",
      video_ids: selection,
      series_name: nomSerie.trim(),
    });
    if (resultat) setSelection([]);
  }

  function basculer(id: number) {
    setSelection((precedents) =>
      precedents.includes(id) ? precedents.filter((x) => x !== id) : [...precedents, id],
    );
  }

  // ── Les boutons d'action, montés une fois et prêtés à la fiche ─────────────

  function actionsDeLaFiche(video: VideoRecord) {
    const menageable = video.class === "nulle" && video.visibility === "public";
    const remasterisable = video.class === "evergreen" && video.visibility === "public";
    if (!menageable && !remasterisable) return null;

    return (
      <div className="biblio-fiche-actions">
        {menageable ? (
          peutFaireLeMenage ? (
            <>
              <button
                className="bouton primaire"
                type="button"
                disabled={pending}
                onClick={() => void faireLeMenage(video)}
              >
                Faire le ménage
                <span className="cout">
                  {libelleCout(actionCost({ type: "menage", video_id: video.id }, state!))}
                </span>
              </button>
              <p className="biblio-fiche-note discret">{NOTE_NULLE_COURTE}</p>
            </>
          ) : (
            <p className="biblio-fiche-note discret">{NOTE_MENAGE_FERME_COURTE}</p>
          )
        ) : null}

        {remasterisable ? (
          <>
            <button
              className="bouton primaire"
              type="button"
              disabled={pending}
              onClick={() => void remasteriser(video)}
            >
              Remasteriser
              <span className="cout">
                {libelleCout(actionCost({ type: "remaster", video_id: video.id }, state!))}
              </span>
            </button>
            <p className="etiquette etiquette-bac">stub — effet moteur pas encore branché</p>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <Panneau titre="📚 Bibliothèque" large>
      {/* ── La rente : le premier revenu passif du jeu, il doit se VOIR ────────
          ⟦C57 · lot 5d⟧ Les chiffres de la semaine, sur une ligne ; le pourquoi, derrière le bouton. */}
      <p className="ligne-info biblio-rente">
        {ligneRenteCourte({
          nb: rente.lignes.length,
          vues: rente.vues,
          abonnes: rente.abonnes,
          argent: rente.argent,
          monetise: rente.monetise,
        })}{" "}
        <DessousBibliotheque
          rente={rente}
          seuilSaturation={RENTE_SATURATION_RANG}
          nullesVisibles={nullesVisibles}
          enTrop={enTrop}
          malusActuel={malusActuel}
          plancher={PLANCHER_NULLES}
          seuilNulles={SEUIL_NULLES}
          coutParNulle={COUT_PAR_NULLE}
          multSerie={RENTE_MULT_SERIE}
          serieMin={SERIE_MIN}
          peutFaireLeMenage={peutFaireLeMenage}
          peutRangerEnSerie={peutRangerEnSerie}
        />
      </p>

      {/* ── Le ménage : le nombre et la conversion, le pourquoi est dessous ─── */}
      {nullesVisibles > 0 ? (
        <p className={`ligne-info ${enTrop > 0 ? "alerte" : ""}`}>
          {ligneNullesCourte({ visibles: nullesVisibles, enTrop, conversion: malusActuel })}
        </p>
      ) : null}

      {state.videos.length === 0 ? (
        <p className="vide">Rien encore. Va tourner quelque chose.</p>
      ) : (
        <div className="biblio-colonnes">
          <div className="biblio-catalogue">
            <table className="catalogue">
              <thead>
                <tr>
                  {peutRangerEnSerie ? <th aria-label="Sélection pour une série" /> : null}
                  <th>Vidéo</th>
                  <th>Statut</th>
                  <th>Vues</th>
                  <th>Abonnés</th>
                  <th>Rente /sem.</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => {
                  const badge = BADGE_CLASSE[video.class];
                  const ligne = rente.lignes.find((l) => l.video.id === video.id);
                  const menageable =
                    peutFaireLeMenage && video.class === "nulle" && video.visibility === "public";
                  const remasterisable =
                    video.class === "evergreen" && video.visibility === "public";
                  const serie = video.series_id
                    ? state.series.find((s) => s.id === video.series_id)
                    : null;

                  return (
                    <tr
                      key={video.id}
                      className={[
                        video.visibility === "prive" ? "privee" : "",
                        affichee?.id === video.id ? "biblio-ligne-ouverte" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {peutRangerEnSerie ? (
                        <td>
                          <input
                            type="checkbox"
                            checked={selection.includes(video.id)}
                            onChange={() => basculer(video.id)}
                            aria-label={`Ajouter « ${nom(video)} » à une série`}
                          />
                        </td>
                      ) : null}
                      <td>
                        {/* ⟦A-9⟧ Le format se lit AVANT le texte : Mathieu ne savait pas qu'il
                            faisait des shorts. Un post-it 9:16, un post-it 16:9. */}
                        <span
                          className={`biblio-format biblio-format-${video.format}`}
                          aria-label={video.format === "short" ? "Short, 9:16" : "Vidéo longue, 16:9"}
                          title={video.format === "short" ? "Short — 9:16" : "Vidéo longue — 16:9"}
                        />
                        <button
                          type="button"
                          className="lien biblio-titre"
                          onClick={() => setOuverte(video.id)}
                        >
                          {nom(video)}
                        </button>
                        {video.visibility === "prive" ? " (privée)" : ""}
                        {serie ? <span className="biblio-serie-tag">série « {serie.name} »</span> : null}
                      </td>
                      <td>
                        {badge.icone} {statutAffichage(video, state.clock_h)}
                      </td>
                      <td>{nombre(video.total_views)}</td>
                      <td>{nombre(video.total_subs)}</td>
                      <td>
                        {ligne
                          ? `${nombre(ligne.vues)} vues${rente.monetise ? ` · ${nombre(ligne.argent)} ₵` : ""}`
                          : "—"}
                      </td>
                      <td className="catalogue-actions">
                        {menageable ? (
                          <button
                            className="bouton discret"
                            type="button"
                            disabled={pending}
                            onClick={() => void faireLeMenage(video)}
                          >
                            Ménage
                          </button>
                        ) : null}
                        {remasterisable ? (
                          <button
                            className="bouton discret"
                            type="button"
                            disabled={pending}
                            onClick={() => void remasteriser(video)}
                          >
                            Remaster
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* ── Les séries ───────────────────────────────────────────────── */}
            <div className="biblio-series">
              <p className="biblio-fiche-sur-titre">📼 Séries</p>
              {state.series.length > 0 ? (
                <ul className="biblio-liste-series">
                  {state.series.map((s) => (
                    <li key={s.id}>
                      {ligneSerieCourte({ nom: s.name, videos: s.video_ids.length, mult: RENTE_MULT_SERIE })}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="biblio-fiche-note discret">{AUCUNE_SERIE_COURTE}</p>
              )}
              {peutRangerEnSerie ? (
                <div className="decoupe-actions">
                  <button
                    className="bouton primaire"
                    type="button"
                    disabled={pending || selection.length < SERIE_MIN}
                    onClick={() => void rangerEnSerie()}
                  >
                    Ranger en série
                    <span className="cout">
                      {selection.length < SERIE_MIN
                        ? `coche au moins ${SERIE_MIN} vidéos`
                        : `${selection.length} vidéos · ${libelleCout(actionCost({ type: "ranger_serie", video_ids: selection, series_name: "" }, state))}`}
                    </span>
                  </button>
                  <p className="etiquette etiquette-bac">stub — effet moteur pas encore branché</p>
                </div>
              ) : (
                <p className="biblio-fiche-note discret">{NOTE_SERIES_FERMEES_COURTE}</p>
              )}
            </div>
          </div>

          {affichee ? (
            <FicheVideo
              key={affichee.id}
              video={affichee}
              titre={nom(affichee)}
              state={state}
              mode={mode}
              rente={renteDeLaFiche}
              monetise={rente.monetise}
              actions={actionsDeLaFiche(affichee)}
            />
          ) : null}
        </div>
      )}

      <p className="ligne-info discret">{CLIN_D_OEIL_COURT}</p>
    </Panneau>
  );
}
